import { useState } from 'react';
import {
  CONTENT_LOCALES,
  translationStatus,
  type ContentLocale,
  type TranslatableString,
} from '@geoic/shared';
import { cn } from '@/lib/cn.js';

const LOCALE_LABELS: Record<ContentLocale, string> = { uz: 'UZ', ru: 'RU', en: 'EN' };

export type TranslatableInputProps = {
  label: string;
  value: TranslatableString;
  onChange: (value: TranslatableString) => void;
  /** `textarea` — uzun matnlar uchun. */
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  hint?: string;
  error?: string;
  /** `uz` majburiy bo'lsa yulduzcha ko'rsatiladi. */
  required?: boolean;
  disabled?: boolean;
};

/**
 * Uch tilli matn maydoni (TZ 4.1).
 *
 * Tillar YORLIQLAR bilan almashadi — uchtasini birdan ko'rsatish forma
 * uzunligini uch barobar oshirardi. To'ldirilmagan tilda yorliq yonida
 * nuqta chiqadi: muharrir tarjimani unutganini darhol ko'radi (D4).
 *
 * `ru` va `en` HECH QACHON majburiy emas — to'ldirilmasa saytda `uz`
 * ko'rsatiladi (CLAUDE.md §4).
 */
export function TranslatableInput({
  label,
  value: raw,
  onChange,
  multiline = false,
  rows = 4,
  placeholder,
  hint,
  error,
  required = false,
  disabled = false,
}: TranslatableInputProps) {
  const [active, setActive] = useState<ContentLocale>('uz');

  // Qiymat TO'LIQ bo'lmasligi mumkin: qoralamadan yoki eski yozuvdan
  // faqat bitta til kelgan bo'lishi mumkin. To'ldirilmasa `value[active]`
  // `undefined` bo'lib, React boshqariladigan maydonni boshqarilmaydiganga
  // aylantirardi — yoki butun ekran yiqilardi.
  const partial = (raw ?? {}) as Partial<TranslatableString>;
  const value: TranslatableString = {
    uz: partial.uz ?? '',
    ru: partial.ru ?? '',
    en: partial.en ?? '',
  };
  const filled = translationStatus(value);

  const controlClass = cn(
    'w-full border bg-white px-3 py-2 text-sm text-n-900',
    'placeholder:text-n-400 disabled:bg-n-50',
    error ? 'border-error-600' : 'border-n-200 focus:border-brand-500',
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-n-800">
          {label}
          {required && <span className="ml-0.5 text-error-600">*</span>}
        </span>

        <div role="tablist" aria-label={`${label} — til`} className="flex gap-1">
          {CONTENT_LOCALES.map((locale) => (
            <button
              key={locale}
              type="button"
              role="tab"
              aria-selected={active === locale}
              onClick={() => setActive(locale)}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 text-xs font-medium transition-colors',
                active === locale
                  ? 'bg-brand-600 text-white'
                  : 'bg-n-100 text-n-600 hover:bg-n-200',
              )}
            >
              {LOCALE_LABELS[locale]}
              {!filled[locale] && (
                <span
                  aria-label="to‘ldirilmagan"
                  className={cn(
                    'size-1.5 rounded-full',
                    active === locale ? 'bg-white/70' : 'bg-warning-600',
                  )}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {multiline ? (
        <textarea
          key={active}
          rows={rows}
          value={value[active]}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={`${label} (${LOCALE_LABELS[active]})`}
          onChange={(event) => onChange({ ...value, [active]: event.target.value })}
          className={controlClass}
        />
      ) : (
        <input
          key={active}
          type="text"
          value={value[active]}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={`${label} (${LOCALE_LABELS[active]})`}
          onChange={(event) => onChange({ ...value, [active]: event.target.value })}
          className={cn(controlClass, 'h-10 py-0')}
        />
      )}

      {error && <p className="text-sm text-error-600">{error}</p>}
      {hint && !error && <p className="text-sm text-n-600">{hint}</p>}

      {active !== 'uz' && !filled[active] && (
        <p className="text-xs text-n-600">To‘ldirilmasa saytda o‘zbekcha matn ko‘rsatiladi.</p>
      )}
    </div>
  );
}

/** Bo'sh uch tilli qiymat — yangi forma uchun. */
export function emptyTranslatable(): TranslatableString {
  return { uz: '', ru: '', en: '' };
}
